/**
 * Keeps the Add to cart button's price in step with the selected purchase option.
 *
 * The Recharge widget renders into a shadow root, so its radios cannot be reached from the
 * document; the selected option is found through the rc-selected attribute the widget sets
 * on the option wrapper, and the prices through the part names it exposes.
 *
 * Everything is re-resolved on each update rather than cached, so a product swap -- which
 * replaces both the widget and the button -- needs no extra wiring.
 */
if (window.PdpPurchasePrice) {

    // product-info re-executes the scripts inside <main> when it swaps a product. The widget
    // and the button are both new by then, so the watcher has to be re-pointed at them --
    // otherwise it stays bound to the shadow root of the page that was just discarded and
    // nothing updates until the visitor touches the widget.
    window.PdpPurchasePrice.start();

} else {

window.PdpPurchasePrice = (() => {
    let observedRoot = null;
    let observer = null;
    let timer = null;
    let deadline = 0;

    // The price parts carry screen-reader-only labels ("Discounted price:"), which would end
    // up in the button otherwise.
    function readPrice(option, part) {
        const element = option.querySelector(`[part~="${part}"]`);
        if (!element) return '';

        const copy = element.cloneNode(true);
        copy.querySelectorAll('.visually-hidden').forEach((node) => node.remove());

        return copy.textContent.trim();
    }

    // The option's own wording, e.g. "Subscribe & Save", sits as a bare text node next to
    // the radio: dropping the element children leaves exactly that.
    function readOptionLabel(option) {
        const selector = option.querySelector('[part~="rc-purchase-option__selector"]');
        if (!selector) return '';

        const copy = selector.cloneNode(true);
        copy.querySelectorAll('*').forEach((node) => node.remove());

        return copy.textContent.trim();
    }

    /**
     * A product swap replaces <main> wholesale, but the outgoing one is only hidden with
     * display:none and removed half a second later. Until then the previous product's widget
     * is still in the document, so a plain document-wide lookup finds it and paints the new
     * product with the old product's purchase option -- which is how a flavor with no
     * subscription at all ended up with the pink "Subscribe & Save" button.
     */
    function isStale(node) {
        if (!node.isConnected) return true;

        // The swap hides the outgoing markup with an inline display:none -- on <main> when the
        // URL changes, on the old <product-info> otherwise -- so that is what is looked for,
        // rather than a computed style a legitimately collapsed widget would also fail.
        for (let element = node; element; element = element.parentElement) {
            if (element.style?.display === 'none') return true;
        }

        return false;
    }

    // Only the widget belonging to the product currently on screen.
    function liveWidgetRoot() {
        const widget = Array.from(document.querySelectorAll('recharge-subscription-widget'))
            .find((element) => !isStale(element));

        return widget?.shadowRoot || null;
    }

    function eachButton(callback) {
        // The sticky bar's copy of the button is excluded: it mirrors the real one already,
        // and writing to both would have the two fighting over the same markup.
        Array.from(document.querySelectorAll('product-info .product-form__submit'))
            .filter((button) => !button.closest('sticky-atc'))
            .forEach(callback);
    }

    /**
     * The one-time state: blue button, the theme's own label and the variant price. Products
     * without a subscription widget never leave it, and one that had a widget a moment ago
     * -- a swap, a widget that failed to render -- is put back into it.
     */
    function reset() {
        document.querySelectorAll('product-info').forEach((info) => {
            info.classList.remove('is-subscription');
        });

        eachButton((button) => {
            if (button.disabled || button.hasAttribute('bis')) return;

            const price = button.querySelector('.product-form__price');
            if (price && button.dataset.defaultPrice) price.textContent = button.dataset.defaultPrice;

            const label = button.querySelector('span:not([class])');
            if (label && button.dataset.defaultLabel) label.textContent = button.dataset.defaultLabel;
        });
    }

    function update() {
        const root = liveWidgetRoot();
        if (!root) return reset();

        // The widget renders for every product it is enabled on, so an empty one -- no
        // purchase options -- is a product without subscriptions rather than a slow render.
        if (!root.querySelector('.rc-purchase-option')) return reset();

        const option = root.querySelector('.rc-purchase-option[rc-selected]');
        if (!option) return;

        const current =
            readPrice(option, 'rc-purchase-option__discounted-price') ||
            readPrice(option, 'rc-purchase-option__price');

        if (!current) return;

        const isSubscription = option.classList.contains('rc-purchase-option__subscription');
        const subscriptionLabel = isSubscription ? readOptionLabel(option) || 'Subscribe & Save' : '';

        // Exposes the state to CSS. product-info also carries .main-product, so the button
        // colours key off .main-product.is-subscription.
        document.querySelectorAll('product-info').forEach((info) => {
            info.classList.toggle('is-subscription', isSubscription);
        });

        eachButton((button) => {
            // Sold out and back-in-stock buttons say something else entirely, and that
            // wording must not be overwritten with a purchase option.
            if (button.disabled || button.hasAttribute('bis')) return;

            // Only the price actually being charged: the struck-through original stays
            // in the widget and is deliberately not repeated on the button.
            const price = button.querySelector('.product-form__price');

            if (price) {
                if (button.dataset.defaultPrice === undefined) {
                    button.dataset.defaultPrice = price.textContent.trim();
                }

                price.textContent = `- ${current}`;
            }

            // The label span is the only one in the button without a class.
            const label = button.querySelector('span:not([class])');
            if (!label) return;

            if (!button.dataset.defaultLabel) {
                button.dataset.defaultLabel = label.textContent.trim();
            }

            label.textContent = subscriptionLabel || button.dataset.defaultLabel;
        });
    }

    function attach() {
        const root = liveWidgetRoot();

        // No widget on this product -- or not rendered yet. Either way the button belongs in
        // its one-time state until one turns up, and the polling below keeps looking.
        if (!root) {
            reset();
            return false;
        }

        if (root !== observedRoot) {
            observer?.disconnect();
            observedRoot = root;
            observer = new MutationObserver(update);
            observer.observe(root, {
                subtree: true,
                childList: true,
                characterData: true,
                attributes: true,
                attributeFilter: ['rc-selected', 'rc-not-selected'],
            });
        }

        update();
        return true;
    }

    // The widget is rendered by the Recharge app, so it is usually not in the DOM yet when
    // this runs -- on a swap it can take a moment to reappear as well. Keep looking until it
    // turns up rather than relying on a single attempt.
    function tick() {
        clearTimeout(timer);

        if (attach()) return;
        if (performance.now() >= deadline) return;

        timer = setTimeout(tick, 250);
    }

    function start() {
        deadline = performance.now() + 15000;
        tick();
    }

    document.addEventListener('change', start);
    window.addEventListener('load', start);
    start();

    return { start, update };
})();

}
